import { isNil } from 'lodash-es';

import type { ProjectMaterialTotal } from '~hooks/useProjects/models';

/**
 * The site-scoped allowed-material set for the order form v3.
 *
 * Mirrors the backend candidate rule: the filter engages only when at least
 * one project material total carries a pickup site; the allowed set is
 * every material with a row at the selected pickup site plus every material
 * with a null-site row ("available anywhere"). Returns null when no filter
 * applies — an all-null (legacy) project always shows its full material list.
 */
export function computeSiteFilteredMaterialIds(
  projectMaterialTotals: ProjectMaterialTotal[],
  pickUpSiteId: string | undefined,
): Set<string> | null {
  // The filter engages only once a pickup site is selected — before that the
  // dispatcher sees the project's full material list (a fully site-linked
  // project would otherwise show an empty list with no explanation).
  if (!pickUpSiteId) {
    return null;
  }

  const projectHasSiteBearingTotals = projectMaterialTotals.some(
    (total) => !isNil(total.site),
  );
  if (!projectHasSiteBearingTotals) {
    return null;
  }

  const allowedMaterialIds = new Set<string>();
  for (const total of projectMaterialTotals) {
    const materialId = total.material?.id;
    if (!materialId) continue;

    const isAvailableAnywhere = isNil(total.site);
    const isAtPickUpSite = total.site?.id === pickUpSiteId;
    if (isAvailableAnywhere || isAtPickUpSite) {
      allowedMaterialIds.add(materialId);
    }
  }

  return allowedMaterialIds;
}
